import React, { useEffect, useState, useRef } from "react";
import { Card, Col, Form, Row, Table, Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { selectJobDetails } from "../../redux/slices/jobDetailsSlice";
import { formatDateForAPI } from "../../utils/dateUtils";

const GPM_TO_LS = 0.063;
const PSI_TO_FT = 2.31;
const FT_TO_M = 0.3048;
const FT_TO_PSI = 0.433;

const silentSlamRows = [
  { id: "ss-2", size: '2"', threshold: 100, slope: 0.0568, intercept: -1.0467 },
  { id: "ss-3", size: '3"', threshold: 225, slope: 0.0257, intercept: -1.1346 },
  { id: "ss-4", size: '4"', threshold: 400, slope: 0.0065, intercept: -0.6578 },
  { id: "ss-5", size: '5"', threshold: 625, slope: 0.0067, intercept: -1.1213 },
  { id: "ss-6", size: '6"', threshold: 900, slope: 0.0051, intercept: -1.014 },
  { id: "ss-8", size: '8"', threshold: 1600, slope: 0.0032, intercept: -1.1624 },
  { id: "ss-10", size: '10"', threshold: 2500, slope: 0.0018, intercept: -1.1592 },
  { id: "ss-12", size: '12"', threshold: 3500, slope: 0.0017, intercept: -2.0011 },
];

const dualDiscRows = [
  { id: "dd-2", size: '2"', threshold: 85, slope: 0.0295, intercept: -0.1265 },
  { id: "dd-2_5", size: '2-1/2"', threshold: 140, slope: 0.0203, intercept: -0.3609 },
  { id: "dd-3", size: '3"', threshold: 230, slope: 0.0103, intercept: -0.1273 },
  { id: "dd-4", size: '4"', threshold: 460, slope: 0.0054, intercept: -0.1751 },
  { id: "dd-5", size: '5"', threshold: 780, slope: 0.0036, intercept: -0.4298 },
  { id: "dd-6", size: '6"', threshold: 1300, slope: 0.0022, intercept: -0.3435 },
  { id: "dd-8", size: '8"', threshold: 2300, slope: 0.0012, intercept: -0.4131 },
  { id: "dd-10", size: '10"', threshold: 4300, slope: 0.0006, intercept: -0.5221 },
  { id: "dd-12", size: '12"', threshold: 6300, slope: 0.0005, intercept: -0.6748 },
  { id: "dd-14", size: '14"', threshold: 8000, slope: 0.0004, intercept: -1.0579 },
];

const lpdRows = [
  { id: "lpd-3", size: '3"', threshold: 236, slope: 0.0074, intercept: -0.6468 },
  { id: "lpd-4", size: '4"', threshold: 460, slope: 0.0031, intercept: -0.4393 },
  { id: "lpd-5", size: '5"', threshold: 600, slope: 0.0021, intercept: -0.3856 },
  { id: "lpd-6", size: '6"', threshold: 952, slope: 0.0014, intercept: -0.3774 },
  { id: "lpd-8", size: '8"', threshold: 1580, slope: 0.0008, intercept: -0.3508 },
  { id: "lpd-10", size: '10"', threshold: 2424, slope: 0.0006, intercept: -0.4096 },
  { id: "lpd-12", size: '12"', threshold: 3577, slope: 0.0004, intercept: -0.4266 },
];

const vafRows = [
  { id: "vaf-v500-4", size: 'V500 4"', threshold: 400, psi: 7 },
  { id: "vaf-v500-6", size: 'V500 6"', threshold: 650, psi: 7 },
  { id: "vaf-v1000-6", size: 'V1000 6"', threshold: 1200, psi: 7 },
  { id: "vaf-v1000-8", size: 'V1000 8"', threshold: 1250, psi: 7 },
  { id: "vaf-v1500-8", size: 'V1500 8"', threshold: 1800, psi: 7 },
  { id: "vaf-v1500-10", size: 'V1500 10"', threshold: 1800, psi: 7 },
  { id: "vaf-v2000-10", size: 'V2000 10"', threshold: 2400, psi: 7 },
];

const forstaRows = [
  { id: "forsta-4", size: 'ABW4-LP 4"', threshold: 500, psi: 7 },
  { id: "forsta-6lp", size: 'ABW6-LP 6"', threshold: 1000, psi: 7 },
  { id: "forsta-6sp", size: 'ABW6-SP 6"', threshold: 1200, psi: 7 },
  { id: "forsta-8", size: 'ABW8-SP 8"', threshold: 1500, psi: 7 },
  { id: "forsta-10", size: 'ABW10-LP 10"', threshold: 2250, psi: 7 },
  { id: "forsta-12", size: 'ABW12-LP 10"', threshold: 2400, psi: 7 },
];

const amiadRows = [
  { id: "amiad-saf1500-2", size: 'SAF 1500 2"', threshold: 135, psi: 5 },
  { id: "amiad-saf1500-3", size: 'SAF 1500 3"', threshold: 302, psi: 5.5 },
  { id: "amiad-saf1500-4", size: 'SAF 1500 4"', threshold: 352, psi: 3.5 },
  { id: "amiad-saf3000-3", size: 'SAF 3000 3"', threshold: 302, psi: 7 },
  { id: "amiad-saf3000-4", size: 'SAF 3000 4"', threshold: 600, psi: 7 },
  { id: "amiad-saf3000-6", size: 'SAF 3000 6"', threshold: 660, psi: 3.5 },
  { id: "amiad-saf4500-4", size: 'SAF 4500 4"', threshold: 600, psi: 8 },
  { id: "amiad-saf4500-6", size: 'SAF 4500 6"', threshold: 1100, psi: 6 },
  { id: "amiad-saf4500-8", size: 'SAF 4500 8"', threshold: 1100, psi: 3.5 },
  { id: "amiad-saf6000-6", size: 'SAF 6000 6"', threshold: 1579, psi: 6 },
  { id: "amiad-saf6000-8", size: 'SAF 6000 8"', threshold: 1760, psi: 5 },
  { id: "amiad-saf6000-10", size: 'SAF 6000 10"', threshold: 1760, psi: 3.25 },
  { id: "amiad-abf3000-3", size: 'ABF-3000 3"', threshold: 302, psi: 5 },
  { id: "amiad-abf3000-4", size: 'ABF-3000 4"', threshold: 600, psi: 6 },
  { id: "amiad-abf3000-6", size: 'ABF-3000 6"', threshold: 660, psi: 4.5 },
  { id: "amiad-abf6000-8", size: 'ABF-6000 8"', threshold: 3100, psi: 10 },
  { id: "amiad-abf6000-10", size: 'ABF-6000 10"', threshold: 3100, psi: 9 },
  { id: "amiad-abf6000-12", size: 'ABF-6000 12"', threshold: 3100, psi: 5 },
  { id: "amiad-abf6000-14", size: 'ABF-6000 14"', threshold: 3100, psi: 3.5 },
];

const bfvRows = [
  { size: '2"', pumpMaxExclusive: 135, dischargeMaxExclusive: 135 },
  { size: '2-1/2"', pumpMaxExclusive: 220, dischargeMaxExclusive: 220 },
  { size: '3"', pumpMaxExclusive: 302, dischargeMaxExclusive: 302 },
  { size: '4"', pumpMaxExclusive: 601, dischargeMaxExclusive: 601 },
  { size: '5"', pumpMaxExclusive: 1022, dischargeMaxExclusive: 1022 },
  { size: '6"', pumpMaxExclusive: 1579, dischargeMaxExclusive: 1579 },
  { size: '8"', pumpMaxExclusive: 3136, dischargeMaxExclusive: 3136 },
  { size: '10"', pumpMaxExclusive: 5340, dischargeMaxExclusive: 5340 },
  { size: '12"', pumpMaxExclusive: 8250, dischargeMaxExclusive: 8250 },
  { size: '14"', pumpMaxExclusive: 11917, dischargeMaxExclusive: 11917 },
  { size: '16"', pumpMaxExclusive: null, dischargeMaxExclusive: 16388 },
  { size: '18"', pumpMaxExclusive: null, dischargeMaxExclusive: 21705 },
  { size: '20"', pumpMaxExclusive: null, dischargeMaxExclusive: 27908 },
  { size: '24"', pumpMaxExclusive: null, dischargeMaxExclusive: 43116 },
];

const defaultChecks = {};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatNum = (value, digits = 2) => {
  if (!Number.isFinite(value)) return "-";
  return value.toFixed(digits);
};

const LossTable = ({ title, rows, flow, selections, onToggle, calcPsi, disabled = false }) => {
  return (
    <Card className="mb-3">
      <Card.Header className="bg-primary text-white">{title}</Card.Header>
      <Card.Body className="p-0">
        <Table size="sm" striped bordered responsive className="mb-0">
          <thead>
            <tr>
              <th>Size</th>
              <th>Select</th>
              <th>PSI Loss</th>
              <th>FT Loss</th>
            </tr>
          </thead>
          <tbody>
            {rows.filter((row) => flow <= row.threshold).map((row) => {
              const isSelected = Boolean(selections[row.id]);
              const psiLoss = isSelected ? calcPsi(row, flow) : 0;
              const ftLoss = psiLoss * PSI_TO_FT;

              return (
                <tr key={row.id}>
                  <td>{row.size}</td>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => onToggle(row.id, e.target.checked)}
                      disabled={disabled}
                    />
                  </td>
                  <td className="calculated-field">{formatNum(psiLoss)}</td>
                  <td className="calculated-field">{formatNum(ftLoss)}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

const VTLossCalculator = ({ useJobData = false }) => {
  const reportRef = useRef();
  const location = useLocation();
  
  // Only use jobId if we're on a job-specific URL (e.g., /1835/additionalItems)
  // Standalone calculator URLs like /engineering should NOT use jobId
  const reduxJobId = useSelector((state) => state.jobId?.jobId);
  const isJobContext = location.pathname.match(/^\/\d+\//);
  const jobId = isJobContext ? reduxJobId : null;
  
  const jobDetails = useSelector(selectJobDetails); // Get job details from Redux
  
  // Only use job data if explicitly enabled via prop AND jobId exists
  const shouldUseJobData = useJobData && jobId;
  
  const [inputs, setInputs] = useState({
    totalFlowGpm: 0,
    pressureAtTerminationPsi: 0,
    mainsQty: 0,
    wetWellDepthFt: 0,
    vafQty: 0,
    forstaQty: 0,
    amiadQty: 0,
    miscFilterQty: 0,
    miscFilterRows: [],
    elbowsCount: 0,
    elevationUpFt: 0,
    abruptPipeChanges: 0,
    teeConnections: 0,
    miscFittings: [],
  });

  const [selections, setSelections] = useState(defaultChecks);
  const [checkValveType, setCheckValveType] = useState('silentSlam'); // 'silentSlam' or 'dualDisc'
  const [filtrationBrand, setFiltrationBrand] = useState('vaf'); // 'vaf', 'forsta', or 'amiad'
  const [filtrationQty, setFiltrationQty] = useState(0);
  const [pumpBfvSize, setPumpBfvSize] = useState("");
  const [dischargeBfvSize, setDischargeBfvSize] = useState("");
  
  // Track which sections should be shown based on job data
  const [showWyeStrainer, setShowWyeStrainer] = useState(true);
  const [showDischargeFilter, setShowDischargeFilter] = useState(true);
  
  // Track which fields were auto-populated from job data
  const [autoPopulatedFields, setAutoPopulatedFields] = useState({
    totalFlowGpm: false,
    pressureAtTerminationPsi: false,
    wetWellDepthFt: false,
    mainsQty: false,
    checkValveType: false,
    filtrationBrand: false,
    filtrationQty: false,
  });

  // Auto-populate form when job data is available
  useEffect(() => {
    if (shouldUseJobData && jobDetails?.generalStation) {
      if (Array.isArray(jobDetails.generalStation) && jobDetails.generalStation.length > 0) {
        const stationData = jobDetails.generalStation[0]; // Get first zone data
        
        // Check WaterTreatmentToggles to determine which sections to show
        let hasWyeStrainer = true; // Default to true for standalone calculator
        let hasDischargeFilter = true; // Default to true for standalone calculator
        
        if (Array.isArray(jobDetails.waterTreatment) && jobDetails.waterTreatment.length > 0) {
          const toggles = jobDetails.waterTreatment.find(t => t.Zone === 1);
          if (toggles) {
            hasWyeStrainer = toggles.HasWyeStrainer === true;
            hasDischargeFilter = toggles.HasDischargeFilter === true;
          }
        }
        
        setShowWyeStrainer(hasWyeStrainer);
        setShowDischargeFilter(hasDischargeFilter);
        
        // Track what gets auto-populated
        const autoPopulated = {
          totalFlowGpm: false,
          pressureAtTerminationPsi: false,
          wetWellDepthFt: false,
          mainsQty: false,
          checkValveType: false,
          filtrationBrand: false,
          filtrationQty: false,
        };
        
        // Extract and parse values, defaulting to 0 if not available
        const totalFlow = parseFloat(stationData.TotalGPM) || 0;
        const dischargePSI = parseFloat(stationData.DischargePSI) || 0;
        
        // Parse wet well depth from various formats:
        // - "24 ft 0 in" or "24 ft 6 in"
        // - "18' 6\"" or "18'6\""
        // - "18" or "18.5" (just feet)
        // - "24 ft" (no inches)
        let wetWellDepth = 0;
        if (stationData.WetWellDepth) {
          const depthStr = stationData.WetWellDepth.toString().trim();
          
          // Try to match feet with "ft" or single quote (e.g., "24 ft", "18'")
          const ftMatch = depthStr.match(/(\d+(?:\.\d+)?)\s*(?:ft|')/i);
          // Try to match inches with "in" or double quote (e.g., "6 in", "6\"")
          const inMatch = depthStr.match(/(\d+(?:\.\d+)?)\s*(?:in|")/i);
          
          if (ftMatch || inMatch) {
            const feet = ftMatch ? parseFloat(ftMatch[1]) : 0;
            const inches = inMatch ? parseFloat(inMatch[1]) : 0;
            wetWellDepth = feet + (inches / 12);
          } else {
            // If no units found, treat as decimal feet (e.g., "18" or "18.5")
            const numValue = parseFloat(depthStr);
            if (!isNaN(numValue)) {
              wetWellDepth = numValue;
            }
          }
        }
        
        if (totalFlow > 0) autoPopulated.totalFlowGpm = true;
        if (dischargePSI > 0) autoPopulated.pressureAtTerminationPsi = true;
        if (wetWellDepth > 0) autoPopulated.wetWellDepthFt = true;
        
        // Get Number of Mains from PumpDetails where Zone: 1 and PumpID: "Main"
        let mainsQty = 0; // Default to 0 if not found
        if (Array.isArray(jobDetails.pumpDetails) && jobDetails.pumpDetails.length > 0) {
          const mainPump = jobDetails.pumpDetails.find(
            pump => pump.Zone === 1 && pump.PumpID === "Main"
          );
          if (mainPump && mainPump.Quantity) {
            mainsQty = parseInt(mainPump.Quantity) || 0;
            if (mainsQty > 0) autoPopulated.mainsQty = true;
          }
        }
        
        // Handle check valve selection
        let checkValveSize = "6"; // Default to 6" (no auto-population available)
        let checkValveTypeValue = "ss"; // Default to Silent Slam (for selection keys)
        let checkValveStateValue = "silentSlam"; // Default for state (silentSlam or dualDisc)
        
        // Get check valve type from PumpParent.MainCheckValveDesign
        if (Array.isArray(jobDetails.pumps) && jobDetails.pumps.length > 0) {
          const pumpParent = jobDetails.pumps.find(p => p.Zone === 1);
          if (pumpParent?.MainCheckValveDesign) {
            const design = pumpParent.MainCheckValveDesign.toLowerCase();
            if (design.includes("silent slam")) {
              checkValveTypeValue = "ss";
              checkValveStateValue = "silentSlam";
              autoPopulated.checkValveType = true;
            } else if (design.includes("dual disc")) {
              checkValveTypeValue = "dd";
              checkValveStateValue = "dualDisc";
              autoPopulated.checkValveType = true;
            }
          }
        }
        
        // Handle Discharge Filter for filtration selection
        let filtrationBrandValue = "vaf"; // Default
        let filtrationQtyValue = 0; // Default to 0 if not found
        let selectedFilterModel = null;
        
        if (Array.isArray(jobDetails.waterTreatment) && jobDetails.waterTreatment.length > 0) {
          const toggles = jobDetails.waterTreatment.find(t => t.Zone === 1);
          
          if (toggles?.HasDischargeFilter && Array.isArray(jobDetails.dischargeFilter) && jobDetails.dischargeFilter.length > 0) {
            const filter = jobDetails.dischargeFilter.find(f => f.Zone === 1);
            if (filter) {
              // Parse quantity
              if (filter.Quantity) {
                filtrationQtyValue = parseInt(filter.Quantity) || 0;
                if (filtrationQtyValue > 0) autoPopulated.filtrationQty = true;
              }
              
              // Match manufacturer to brand (case-insensitive)
              const manufacturer = (filter.Manufacturer || "").toLowerCase();
              if (manufacturer === "vaf") {
                filtrationBrandValue = "vaf";
                autoPopulated.filtrationBrand = true;
              } else if (manufacturer === "forsta" || manufacturer === "tekleen") {
                filtrationBrandValue = "forsta";
                autoPopulated.filtrationBrand = true;
              } else if (manufacturer === "amiad") {
                filtrationBrandValue = "amiad";
                autoPopulated.filtrationBrand = true;
              }
              
              // Store model for auto-selection (Model field contains the exact value)
              selectedFilterModel = filter.Model;
            }
          }
        }
        
        // Build selected checks - only include auto-populated items
        const newChecks = {};
        
        // Only add check valve if type was auto-populated
        if (autoPopulated.checkValveType) {
          newChecks[`${checkValveTypeValue}-${checkValveSize}`] = true;
        }
        
        // Add filter model if found - try to match to available calculator models
        if (selectedFilterModel && autoPopulated.filtrationBrand) {
          let filterKey = null;
          const modelLower = selectedFilterModel.toLowerCase();
          
          if (filtrationBrandValue === "vaf") {
            // VAF models: database has "v1000-8", calculator expects "vaf-v1000-8"
            if (modelLower.match(/^v\d+/)) {
              filterKey = `vaf-${modelLower}`;
            }
          } else if (filtrationBrandValue === "forsta") {
            // Forsta/Tekleen models: database has "d10-180-316l", need to extract size
            // Try to match based on size in model name
            const forstaMatch = modelLower.match(/^d(\d+)/);
            if (forstaMatch && forstaMatch[1]) {
              const size = forstaMatch[1];
              // Map to available forsta models
              const sizeMap = {
                "4": "forsta-4",
                "6": "forsta-6lp", // Default to LP variant
                "8": "forsta-8",
                "10": "forsta-10",
                "12": "forsta-12"
              };
              filterKey = sizeMap[size];
            }
          } else if (filtrationBrandValue === "amiad") {
            // Amiad models would need similar mapping if provided
            // For now, leave as null since we don't have amiad in the manufacturer list
          }
          
          if (filterKey) {
            newChecks[filterKey] = true;
          }
        }
        
        setSelections(newChecks);
        setCheckValveType(checkValveStateValue);
        setFiltrationBrand(filtrationBrandValue);
        setFiltrationQty(filtrationQtyValue);
        setAutoPopulatedFields(autoPopulated);
        
        setInputs(prev => ({
          ...prev,
          totalFlowGpm: totalFlow,
          pressureAtTerminationPsi: dischargePSI,
          wetWellDepthFt: wetWellDepth,
          mainsQty: mainsQty,
          [`${filtrationBrandValue}Qty`]: filtrationQtyValue
        }));
      }
    } else if (!jobId) {
      // Reset to defaults for standalone calculator mode
      setShowWyeStrainer(true);
      setShowDischargeFilter(true);
    }
  }, [jobId, jobDetails?.generalStation, jobDetails?.pumpDetails, jobDetails?.pumps, jobDetails?.waterTreatment, jobDetails?.dischargeFilter, shouldUseJobData]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalFlowGpm = toNumber(inputs.totalFlowGpm);
  const totalFlowLs = totalFlowGpm * GPM_TO_LS;
  const pressurePsi = toNumber(inputs.pressureAtTerminationPsi);
  const pressureFt = pressurePsi * PSI_TO_FT;
  const pressureM = pressureFt * FT_TO_M;

  const mainsQty = toNumber(inputs.mainsQty);
  const flowEachMainGpm = mainsQty === 0 ? 0 : totalFlowGpm / mainsQty;
  const flowEachMainLs = mainsQty === 0 ? 0 : totalFlowLs / mainsQty;

  const lowLevelLiftFt = toNumber(inputs.wetWellDepthFt) === 0 ? 0 : toNumber(inputs.wetWellDepthFt) - 4.667;
  const liftTotalFt = pressureFt + lowLevelLiftFt;

  const toggleSelection = (id, checked) => {
    setSelections((prev) => ({ ...prev, [id]: checked }));
  };

  const linearPsi = (row, flow) => row.slope * flow + row.intercept;

  const computeGroupFt = (rows, flow, psiFn) =>
    rows.reduce((sum, row) => {
      const available = flow <= row.threshold;
      const selected = Boolean(selections[row.id]);
      if (!available || !selected) return sum;
      return sum + psiFn(row, flow) * PSI_TO_FT;
    }, 0);

  const silentFt = computeGroupFt(silentSlamRows, flowEachMainGpm, linearPsi);
  const dualFt = computeGroupFt(dualDiscRows, flowEachMainGpm, linearPsi);
  const selectedCheckValveFt = checkValveType === 'silentSlam' ? silentFt : dualFt;
  const checkValveFt = liftTotalFt + selectedCheckValveFt;

  const lpdFt = showWyeStrainer ? computeGroupFt(lpdRows, totalFlowGpm, linearPsi) : 0;

  const filtrationPerFilterFlow = toNumber(filtrationQty) > 0 ? totalFlowGpm / toNumber(filtrationQty) : 0;

  const vafFt = (showDischargeFilter && filtrationBrand === 'vaf') ? computeGroupFt(vafRows, filtrationPerFilterFlow, (row) => row.psi) : 0;
  const forstaFt = (showDischargeFilter && filtrationBrand === 'forsta') ? computeGroupFt(forstaRows, filtrationPerFilterFlow, (row) => row.psi) : 0;
  const amiadFt = (showDischargeFilter && filtrationBrand === 'amiad') ? computeGroupFt(amiadRows, filtrationPerFilterFlow, (row) => row.psi) : 0;
  const selectedFiltrationFt = vafFt + forstaFt + amiadFt;

  const miscFilterFt = inputs.miscFilterRows.reduce((sum, row) => sum + toNumber(row.psi) * PSI_TO_FT, 0);

  const filtrationTotalFt = checkValveFt + lpdFt + selectedFiltrationFt + miscFilterFt;

  const pumpBfvMeta = bfvRows.find((row) => row.size === pumpBfvSize);
  const dischargeBfvMeta = bfvRows.find((row) => row.size === dischargeBfvSize);

  const pumpBfvAvailable =
    pumpBfvMeta && pumpBfvMeta.pumpMaxExclusive !== null
      ? flowEachMainGpm < pumpBfvMeta.pumpMaxExclusive
      : false;
  const dischargeBfvAvailable =
    dischargeBfvMeta && dischargeBfvMeta.dischargeMaxExclusive !== null
      ? totalFlowGpm > 0 && totalFlowGpm < dischargeBfvMeta.dischargeMaxExclusive
      : false;

  useEffect(() => {
    if (!pumpBfvAvailable) setPumpBfvSize("");
  }, [pumpBfvAvailable]);

  useEffect(() => {
    if (!dischargeBfvAvailable) setDischargeBfvSize("");
  }, [dischargeBfvAvailable]);

  const selectedBfvCount = (pumpBfvSize ? 1 : 0) + (dischargeBfvSize ? 1 : 0);
  const bfvFt = selectedBfvCount * PSI_TO_FT;
  const bfvTotalFt = filtrationTotalFt + bfvFt;

  const transitionPsi =
    toNumber(inputs.elbowsCount) * 0.5 +
    toNumber(inputs.elevationUpFt) * FT_TO_PSI +
    toNumber(inputs.abruptPipeChanges) * 0.5 +
    toNumber(inputs.teeConnections) * 0.5;

  const transitionFt =
    toNumber(inputs.elbowsCount) * 0.5 * PSI_TO_FT +
    toNumber(inputs.elevationUpFt) +
    toNumber(inputs.abruptPipeChanges) * 0.5 * PSI_TO_FT +
    toNumber(inputs.teeConnections) * 0.5 * PSI_TO_FT;

  const miscFittingsFt = inputs.miscFittings.reduce(
    (sum, row) => sum + toNumber(row.qty) * toNumber(row.psi) * PSI_TO_FT,
    0
  );

  const finalTotalFt = bfvTotalFt + transitionFt + miscFittingsFt;
  const finalTotalM = finalTotalFt * FT_TO_M;
  const finalTotalPsi = finalTotalFt * FT_TO_PSI;

  const sustainTotalFt = totalFlowGpm === 0 ? 0 : finalTotalFt - 10;

  const diffFt = finalTotalFt - pressureFt;
  const diffPsi = finalTotalPsi - pressurePsi;
  const diffM = finalTotalM - pressureM;

  const updateInput = (key, value) => setInputs((prev) => ({ ...prev, [key]: value }));

  const updateRow = (listKey, index, field, value) => {
    setInputs((prev) => {
      const copy = [...prev[listKey]];
      copy[index] = { ...copy[index], [field]: value };
      return { ...prev, [listKey]: copy };
    });
  };

  const addMiscFilterRow = () => {
    setInputs((prev) => ({
      ...prev,
      miscFilterRows: [...prev.miscFilterRows, { type: "", psi: 0 }]
    }));
  };

  const removeMiscFilterRow = (index) => {
    setInputs((prev) => ({
      ...prev,
      miscFilterRows: prev.miscFilterRows.filter((_, idx) => idx !== index)
    }));
  };

  const addMiscFittingRow = () => {
    setInputs((prev) => ({
      ...prev,
      miscFittings: [...prev.miscFittings, { type: "", qty: 0, psi: 0 }]
    }));
  };

  const removeMiscFittingRow = (index) => {
    setInputs((prev) => ({
      ...prev,
      miscFittings: prev.miscFittings.filter((_, idx) => idx !== index)
    }));
  };

  const getSelectedModels = () => {
    const selected = [];
    
    // Check Valve
    const checkValveRows = checkValveType === 'silentSlam' ? silentSlamRows : dualDiscRows;
    checkValveRows.forEach(row => {
      if (selections[row.id] && flowEachMainGpm <= row.threshold) {
        selected.push(`${checkValveType === 'silentSlam' ? 'Silent Slam' : 'Dual Disc'} ${row.size}`);
      }
    });
    
    // LPD
    lpdRows.forEach(row => {
      if (selections[row.id] && totalFlowGpm <= row.threshold) {
        selected.push(`LPD ${row.size}`);
      }
    });
    
    // Filtration
    const filtrationRows = filtrationBrand === 'vaf' ? vafRows : filtrationBrand === 'forsta' ? forstaRows : amiadRows;
    filtrationRows.forEach(row => {
      if (selections[row.id] && filtrationPerFilterFlow <= row.threshold) {
        const brandName = filtrationBrand === 'vaf' ? 'VAF' : filtrationBrand === 'forsta' ? 'Forsta/Tekleen' : 'Amiad';
        selected.push(`${brandName} ${row.size}`);
      }
    });
    
    return selected;
  };

  const handleExportToHTML = () => {
    if (!reportRef.current) return;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>VT Loss Calculator Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h2 { color: #0066cc; border-bottom: 2px solid #0066cc; padding-bottom: 10px; }
            h3 { color: #333; margin-top: 20px; }
            table { width: 100%; border-collapse: collapse; margin: 15px 0; }
            th, td { padding: 8px; text-align: left; border: 1px solid #ddd; }
            th { background-color: #0066cc; color: white; }
            .summary-section { margin: 20px 0; padding: 15px; background-color: #f8f9fa; border-radius: 5px; }
            .summary-item { margin: 10px 0; }
            .label { font-weight: 600; }
          </style>
        </head>
        <body>
          ${reportRef.current.innerHTML}
        </body>
      </html>
    `;

    // Generate filename based on job context
    let filename;
    const currentDate = formatDateForAPI(new Date());
    
    if (shouldUseJobData && jobDetails?.generalProject?.[0]) {
      const jobNumber = jobDetails.generalProject[0].JobNumber || 'Unknown';
      const projectName = (jobDetails.generalProject[0].ProjectName || 'Unknown')
        .replace(/[^a-zA-Z0-9]/g, '_'); // Replace special chars with underscore
      filename = `${jobNumber}_${projectName}_VTLossesCalc_${currentDate}.html`;
    } else {
      filename = `VT_Loss_Calculator_${currentDate}.html`;
    }

    const blob = new Blob([htmlContent], { type: "text/html" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="vt-loss-calculator">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Main VT Losses Calculator</h4>
        <div>
          <Button variant="success" size="sm" onClick={handleExportToHTML}>
            <i className="bi bi-file-earmark-text me-1"></i> Export to HTML
          </Button>
        </div>
      </div>

      <Card className="mb-3">
        <Card.Header className="bg-primary text-white">System Overall Specs</Card.Header>
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Total Flow (GPM) <span className="text-danger">*</span></Form.Label>
                <Form.Control type="number" className={autoPopulatedFields.totalFlowGpm ? "calculated-field" : "input-field"} value={inputs.totalFlowGpm} onChange={(e) => updateInput("totalFlowGpm", e.target.value)} readOnly={autoPopulatedFields.totalFlowGpm} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Pressure At Termination (PSI) <span className="text-danger">*</span></Form.Label>
                <Form.Control type="number" className={autoPopulatedFields.pressureAtTerminationPsi ? "calculated-field" : "input-field"} value={inputs.pressureAtTerminationPsi} onChange={(e) => updateInput("pressureAtTerminationPsi", e.target.value)} readOnly={autoPopulatedFields.pressureAtTerminationPsi} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label># Of Mains <span className="text-danger">*</span></Form.Label>
                <Form.Control type="number" className={autoPopulatedFields.mainsQty ? "calculated-field" : "input-field"} value={inputs.mainsQty} onChange={(e) => updateInput("mainsQty", e.target.value)} readOnly={autoPopulatedFields.mainsQty} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Wet Well Depth (FT) <span className="text-danger">*</span></Form.Label>
                <Form.Control type="number" className={autoPopulatedFields.wetWellDepthFt ? "calculated-field" : "input-field"} value={inputs.wetWellDepthFt} onChange={(e) => updateInput("wetWellDepthFt", e.target.value)} readOnly={autoPopulatedFields.wetWellDepthFt} />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Total Flow (L/S)</Form.Label>
                <Form.Control className="calculated-field" value={formatNum(totalFlowLs)} readOnly />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Pressure At Termination (FT / M)</Form.Label>
                <Form.Control className="calculated-field" value={`${formatNum(pressureFt)} / ${formatNum(pressureM)}`} readOnly />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Flow Each Main (GPM / L/S)</Form.Label>
                <Form.Control className="calculated-field" value={`${formatNum(flowEachMainGpm)} / ${formatNum(flowEachMainLs)}`} readOnly />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Lift Total (FT)</Form.Label>
                <Form.Control className="calculated-field" value={formatNum(liftTotalFt)} readOnly />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header className="bg-primary text-white">Check Valve</Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Check Valve Type <span className="text-danger">*</span></Form.Label>
                <Form.Select className={shouldUseJobData ? "calculated-field" : "input-field"} value={checkValveType} onChange={(e) => setCheckValveType(e.target.value)} disabled={shouldUseJobData}>
                  <option value="silentSlam">Silent Slam</option>
                  <option value="dualDisc">Dual Disc</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          {checkValveType === 'silentSlam' ? (
            <LossTable title="Silent Slam Models" rows={silentSlamRows} flow={flowEachMainGpm} selections={selections} onToggle={toggleSelection} calcPsi={linearPsi} />
          ) : (
            <LossTable title="Dual Disc Models" rows={dualDiscRows} flow={flowEachMainGpm} selections={selections} onToggle={toggleSelection} calcPsi={linearPsi} />
          )}
          <div className="bg-light p-2 rounded mt-2">
            <strong>Check Valve Total:</strong> {formatNum(checkValveFt)} ft / {formatNum(checkValveFt * FT_TO_M)} m / {formatNum(checkValveFt * FT_TO_PSI)} psi
          </div>
        </Card.Body>
      </Card>

      {showWyeStrainer ? (
        <LossTable title="Filtration: LPD Wye Strainer" rows={lpdRows} flow={totalFlowGpm} selections={selections} onToggle={toggleSelection} calcPsi={linearPsi} />
      ) : (
        <Card className="mb-3">
          <Card.Header className="bg-secondary text-white">Filtration: LPD Wye Strainer</Card.Header>
          <Card.Body className="text-center text-muted">
            <p className="mb-0">No Wye Strainer selected for this job</p>
          </Card.Body>
        </Card>
      )}

      {showDischargeFilter ? (
        <Card className="mb-3">
          <Card.Header className="bg-primary text-white">Filtration: Media Filter</Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Label>Filter Brand <span className="text-danger">*</span></Form.Label>
                <Form.Select className={shouldUseJobData ? "calculated-field" : "input-field"} value={filtrationBrand} onChange={(e) => setFiltrationBrand(e.target.value)} disabled={shouldUseJobData}>
                  <option value="vaf">VAF</option>
                  <option value="forsta">Forsta / Tekleen</option>
                  <option value="amiad">Amiad</option>
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Label>Qty Of Filters <span className="text-danger">*</span></Form.Label>
                <Form.Control type="number" className={shouldUseJobData ? "calculated-field" : "input-field"} value={filtrationQty} onChange={(e) => setFiltrationQty(e.target.value)} readOnly={shouldUseJobData} />
              </Col>
              <Col md={4}>
                <Form.Label>GPM Per Filter</Form.Label>
                <Form.Control className="calculated-field" value={formatNum(filtrationPerFilterFlow)} readOnly />
              </Col>
            </Row>
            {filtrationBrand === 'vaf' && (
              <LossTable title="VAF Models" rows={vafRows} flow={filtrationPerFilterFlow} selections={selections} onToggle={toggleSelection} calcPsi={(r) => r.psi} />
            )}
            {filtrationBrand === 'forsta' && (
              <LossTable title="Forsta / Tekleen Models" rows={forstaRows} flow={filtrationPerFilterFlow} selections={selections} onToggle={toggleSelection} calcPsi={(r) => r.psi} />
            )}
            {filtrationBrand === 'amiad' && (
              <LossTable title="Amiad Models" rows={amiadRows} flow={filtrationPerFilterFlow} selections={selections} onToggle={toggleSelection} calcPsi={(r) => r.psi} />
            )}
          </Card.Body>
        </Card>
      ) : (
        <Card className="mb-3">
          <Card.Header className="bg-secondary text-white">Filtration: Media Filter</Card.Header>
          <Card.Body className="text-center text-muted">
            <p className="mb-0">No Discharge Filter selected for this job</p>
          </Card.Body>
        </Card>
      )}

      <Card className="mb-3">
        <Card.Header className="bg-primary text-white">Filtration: Misc Filters</Card.Header>
        <Card.Body>
          <Row className="mb-2">
            <Col md={3}>
              <Form.Label>Qty Of Filters <span className="text-danger">*</span></Form.Label>
              <Form.Control type="number" className="input-field" value={inputs.miscFilterQty} onChange={(e) => updateInput("miscFilterQty", e.target.value)} />
            </Col>
          </Row>
          <div className="mb-2">
            <Button variant="primary" size="sm" onClick={addMiscFilterRow}>
              <i className="bi bi-plus-circle me-1"></i> Add Filter
            </Button>
          </div>
          {inputs.miscFilterRows.length > 0 && (
            <Table size="sm" bordered>
              <thead>
                <tr>
                  <th>Size / Type</th>
                  <th>PSI Loss</th>
                  <th>FT Loss</th>
                  <th style={{ width: '80px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {inputs.miscFilterRows.map((row, idx) => (
                  <tr key={`mf-${idx}`}>
                    <td>
                      <Form.Control className="input-field" value={row.type} onChange={(e) => updateRow("miscFilterRows", idx, "type", e.target.value)} />
                    </td>
                    <td>
                      <Form.Control type="number" className="input-field" value={row.psi} onChange={(e) => updateRow("miscFilterRows", idx, "psi", e.target.value)} />
                    </td>
                    <td className="calculated-field">{formatNum(toNumber(row.psi) * PSI_TO_FT)}</td>
                    <td>
                      <Button variant="danger" size="sm" onClick={() => removeMiscFilterRow(idx)}>
                        <i className="bi bi-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          <div className="bg-light p-2 rounded">
            <strong>Filtration Total:</strong> {formatNum(filtrationTotalFt)} ft / {formatNum(filtrationTotalFt * FT_TO_M)} m / {formatNum(filtrationTotalFt * FT_TO_PSI)} psi
          </div>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header className="bg-primary text-white">Butterfly Valve Selection</Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Label>Pump BFV (based on one pumpset flow) <span className="text-danger">*</span></Form.Label>
              <Form.Select className="input-field" value={pumpBfvSize} onChange={(e) => setPumpBfvSize(e.target.value)}>
                <option value="">None</option>
                {bfvRows
                  .filter((r) => r.pumpMaxExclusive !== null && flowEachMainGpm < r.pumpMaxExclusive)
                  .map((r) => (
                    <option key={`pump-${r.size}`} value={r.size}>
                      {r.size}
                    </option>
                  ))}
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>Discharge BFV (based on total flow) <span className="text-danger">*</span></Form.Label>
              <Form.Select className="input-field" value={dischargeBfvSize} onChange={(e) => setDischargeBfvSize(e.target.value)}>
                <option value="">None</option>
                {bfvRows
                  .filter((r) => r.dischargeMaxExclusive !== null && totalFlowGpm > 0 && totalFlowGpm < r.dischargeMaxExclusive)
                  .map((r) => (
                    <option key={`disch-${r.size}`} value={r.size}>
                      {r.size}
                    </option>
                  ))}
              </Form.Select>
            </Col>
          </Row>
          <div className="mt-3 bg-light p-2 rounded">
            <div><strong>BFV Loss:</strong> {formatNum(bfvFt)} ft ({selectedBfvCount} valve(s) at 1 psi each)</div>
            <div><strong>Total Through BFV Section:</strong> {formatNum(bfvTotalFt)} ft</div>
          </div>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header className="bg-primary text-white">Pipe Transitions / Misc Fittings</Card.Header>
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Label># Elbows (90/45) <span className="text-danger">*</span></Form.Label>
              <Form.Control type="number" className="input-field" value={inputs.elbowsCount} onChange={(e) => updateInput("elbowsCount", e.target.value)} />
            </Col>
            <Col md={3}>
              <Form.Label>Elevation Up (FT) <span className="text-danger">*</span></Form.Label>
              <Form.Control type="number" className="input-field" value={inputs.elevationUpFt} onChange={(e) => updateInput("elevationUpFt", e.target.value)} />
            </Col>
            <Col md={3}>
              <Form.Label>Abrupt Pipe Changes <span className="text-danger">*</span></Form.Label>
              <Form.Control type="number" className="input-field" value={inputs.abruptPipeChanges} onChange={(e) => updateInput("abruptPipeChanges", e.target.value)} />
            </Col>
            <Col md={3}>
              <Form.Label>Tee Connections <span className="text-danger">*</span></Form.Label>
              <Form.Control type="number" className="input-field" value={inputs.teeConnections} onChange={(e) => updateInput("teeConnections", e.target.value)} />
            </Col>
          </Row>
          <div className="mt-2 bg-light p-2 rounded">Transition Loss: {formatNum(transitionFt)} ft / {formatNum(transitionPsi)} psi</div>

          <div className="mt-3 mb-2">
            <Button variant="primary" size="sm" onClick={addMiscFittingRow}>
              <i className="bi bi-plus-circle me-1"></i> Add Component
            </Button>
          </div>
          {inputs.miscFittings.length > 0 && (
            <Table size="sm" bordered>
              <thead>
                <tr>
                  <th>Misc Component</th>
                  <th>Qty</th>
                  <th>PSI Loss</th>
                  <th>Total PSI</th>
                  <th>Total FT</th>
                  <th style={{ width: '80px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {inputs.miscFittings.map((row, idx) => {
                  const totalPsi = toNumber(row.qty) * toNumber(row.psi);
                  const totalFt = totalPsi * PSI_TO_FT;
                  return (
                    <tr key={`misc-${idx}`}>
                      <td><Form.Control className="input-field" value={row.type} onChange={(e) => updateRow("miscFittings", idx, "type", e.target.value)} /></td>
                      <td><Form.Control type="number" className="input-field" value={row.qty} onChange={(e) => updateRow("miscFittings", idx, "qty", e.target.value)} /></td>
                      <td><Form.Control type="number" className="input-field" value={row.psi} onChange={(e) => updateRow("miscFittings", idx, "psi", e.target.value)} /></td>
                      <td className="calculated-field">{formatNum(totalPsi)}</td>
                      <td className="calculated-field">{formatNum(totalFt)}</td>
                      <td>
                        <Button variant="danger" size="sm" onClick={() => removeMiscFittingRow(idx)}>
                          <i className="bi bi-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header className="bg-success text-white">Station Summary</Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <div className="summary-item"><strong>Original Requirements:</strong> {formatNum(totalFlowGpm, 0)} GPM / {formatNum(pressureFt)} ft / {formatNum(pressurePsi)} psi / {formatNum(pressureM)} m</div>
              <div className="summary-item"><strong>Final Total Losses:</strong> {formatNum(finalTotalFt)} ft / {formatNum(finalTotalPsi)} psi / {formatNum(finalTotalM)} m</div>
              <div className="summary-item"><strong>Difference Of Losses:</strong> {formatNum(diffFt)} ft / {formatNum(diffPsi)} psi / {formatNum(diffM)} m</div>
            </Col>
            <Col md={6}>
              <div className="summary-item"><strong>Pressure Maintenance Pump Specs (if applicable):</strong> {formatNum(sustainTotalFt)} ft / {formatNum(sustainTotalFt * FT_TO_M)} m / {formatNum(sustainTotalFt * FT_TO_PSI)} psi</div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Hidden Report Section for Export */}
      <div ref={reportRef} style={{ display: 'none' }}>
        <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
          <h2 style={{ color: '#0066cc', borderBottom: '2px solid #0066cc', paddingBottom: '10px' }}>VT Loss Calculator Report</h2>
          <p style={{ color: '#666', marginBottom: '20px' }}>Generated: {new Date().toLocaleString()}</p>
          
          <div style={{ marginBottom: '30px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
            <h3 style={{ color: '#333', marginTop: 0 }}>System Specifications</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', margin: '10px 0' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '8px', fontWeight: '600', width: '40%' }}>Total Flow (GPM):</td>
                  <td style={{ padding: '8px' }}>{formatNum(totalFlowGpm, 0)}</td>
                  <td style={{ padding: '8px', fontWeight: '600' }}>Total Flow (L/S):</td>
                  <td style={{ padding: '8px' }}>{formatNum(totalFlowLs)}</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', fontWeight: '600' }}>Pressure At Termination (PSI):</td>
                  <td style={{ padding: '8px' }}>{formatNum(pressurePsi)}</td>
                  <td style={{ padding: '8px', fontWeight: '600' }}>Pressure (FT / M):</td>
                  <td style={{ padding: '8px' }}>{formatNum(pressureFt)} / {formatNum(pressureM)}</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', fontWeight: '600' }}># Of Mains:</td>
                  <td style={{ padding: '8px' }}>{mainsQty}</td>
                  <td style={{ padding: '8px', fontWeight: '600' }}>Flow Each Main (GPM / L/S):</td>
                  <td style={{ padding: '8px' }}>{formatNum(flowEachMainGpm)} / {formatNum(flowEachMainLs)}</td>
                </tr>
                <tr>
                  <td style={{ padding: '8px', fontWeight: '600' }}>Wet Well Depth (FT):</td>
                  <td style={{ padding: '8px' }}>{formatNum(toNumber(inputs.wetWellDepthFt))}</td>
                  <td style={{ padding: '8px', fontWeight: '600' }}>Lift Total (FT):</td>
                  <td style={{ padding: '8px' }}>{formatNum(liftTotalFt)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={{ marginBottom: '30px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
            <h3 style={{ color: '#333', marginTop: 0 }}>Selected Components</h3>
            <div style={{ marginBottom: '15px' }}>
              <strong style={{ display: 'block', marginBottom: '5px' }}>Check Valve Type:</strong>
              <span>{checkValveType === 'silentSlam' ? 'Silent Slam' : 'Dual Disc'}</span>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <strong style={{ display: 'block', marginBottom: '5px' }}>Filtration Brand:</strong>
              <span>{filtrationBrand === 'vaf' ? 'VAF' : filtrationBrand === 'forsta' ? 'Forsta / Tekleen' : 'Amiad'} ({filtrationQty} filters @ {formatNum(filtrationPerFilterFlow)} GPM each)</span>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <strong style={{ display: 'block', marginBottom: '5px' }}>Butterfly Valves:</strong>
              <span>Pump: {pumpBfvSize || 'None'} | Discharge: {dischargeBfvSize || 'None'}</span>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <strong style={{ display: 'block', marginBottom: '5px' }}>Selected Models:</strong>
              <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                {getSelectedModels().map((model, idx) => (
                  <li key={idx}>{model}</li>
                ))}
              </ul>
            </div>
          </div>

          <div style={{ marginBottom: '30px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
            <h3 style={{ color: '#333', marginTop: 0 }}>Loss Breakdown</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#0066cc', color: 'white' }}>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Component</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>FT Loss</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>PSI Loss</th>
                  <th style={{ padding: '10px', textAlign: 'right' }}>M Loss</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ backgroundColor: '#fff' }}>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>Check Valve</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>{formatNum(selectedCheckValveFt)}</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>{formatNum(selectedCheckValveFt * FT_TO_PSI)}</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>{formatNum(selectedCheckValveFt * FT_TO_M)}</td>
                </tr>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>LPD Wye Strainer</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>{formatNum(lpdFt)}</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>{formatNum(lpdFt * FT_TO_PSI)}</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>{formatNum(lpdFt * FT_TO_M)}</td>
                </tr>
                <tr style={{ backgroundColor: '#fff' }}>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>Media Filter ({filtrationBrand.toUpperCase()})</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>{formatNum(selectedFiltrationFt)}</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>{formatNum(selectedFiltrationFt * FT_TO_PSI)}</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>{formatNum(selectedFiltrationFt * FT_TO_M)}</td>
                </tr>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>Butterfly Valves</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>{formatNum(bfvFt)}</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>{formatNum(bfvFt * FT_TO_PSI)}</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>{formatNum(bfvFt * FT_TO_M)}</td>
                </tr>
                <tr style={{ backgroundColor: '#fff' }}>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>Pipe Transitions</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>{formatNum(transitionFt)}</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>{formatNum(transitionPsi)}</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>{formatNum(transitionFt * FT_TO_M)}</td>
                </tr>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <td style={{ padding: '8px', border: '1px solid #ddd' }}>Misc Fittings</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>{formatNum(miscFittingsFt)}</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>{formatNum(miscFittingsFt * FT_TO_PSI)}</td>
                  <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>{formatNum(miscFittingsFt * FT_TO_M)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#d4edda', borderRadius: '5px', border: '2px solid #28a745' }}>
            <h3 style={{ color: '#155724', marginTop: 0 }}>Final Station Summary</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '10px', fontWeight: '600', fontSize: '16px' }}>Original Requirements:</td>
                  <td style={{ padding: '10px', fontSize: '16px' }}>{formatNum(totalFlowGpm, 0)} GPM / {formatNum(pressureFt)} ft / {formatNum(pressurePsi)} psi / {formatNum(pressureM)} m</td>
                </tr>
                <tr>
                  <td style={{ padding: '10px', fontWeight: '600', fontSize: '16px' }}>Final Total Losses:</td>
                  <td style={{ padding: '10px', fontSize: '16px' }}>{formatNum(finalTotalFt)} ft / {formatNum(finalTotalPsi)} psi / {formatNum(finalTotalM)} m</td>
                </tr>
                <tr>
                  <td style={{ padding: '10px', fontWeight: '600', fontSize: '16px' }}>Difference Of Losses:</td>
                  <td style={{ padding: '10px', fontSize: '16px', color: diffFt > 0 ? '#dc3545' : '#28a745' }}>{formatNum(diffFt)} ft / {formatNum(diffPsi)} psi / {formatNum(diffM)} m</td>
                </tr>
                <tr>
                  <td style={{ padding: '10px', fontWeight: '600', fontSize: '16px' }}>Pressure Maintenance Pump Specs:</td>
                  <td style={{ padding: '10px', fontSize: '16px' }}>{formatNum(sustainTotalFt)} ft / {formatNum(sustainTotalFt * FT_TO_M)} m / {formatNum(sustainTotalFt * FT_TO_PSI)} psi</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <style jsx>{`
        .vt-loss-calculator {
          padding: 20px;
        }

        .input-field {
          background-color: #ffffff !important;
        }

        .calculated-field {
          background-color: #e9ecef !important;
          color: #495057 !important;
          cursor: not-allowed;
        }

        .form-control.calculated-field:focus {
          background-color: #e9ecef !important;
          box-shadow: none;
        }

        .form-label {
          font-weight: 500;
          font-size: 0.9rem;
          margin-bottom: 0.25rem;
        }

        .text-danger {
          color: #dc3545;
        }

        .card-header.bg-primary {
          background-color: #0066cc !important;
          font-weight: 600;
        }

        .card-header.bg-success {
          background-color: #28a745 !important;
          font-weight: 600;
        }

        .summary-item {
          padding: 8px 0;
          border-bottom: 1px solid #dee2e6;
        }

        .summary-item:last-child {
          border-bottom: none;
        }

        tbody td.calculated-field {
          background-color: #f8f9fa;
          font-family: 'Courier New', monospace;
        }
      `}</style>
    </div>
  );
};

export default VTLossCalculator;
